import { useCallback, useEffect, useMemo, useRef, useState } from 'react'
import Header from './components/Header'
import Hero from './components/Hero'
import Services from './components/Services'
import Portfolio from './components/Portfolio'
import About from './components/About'
import Partners from './components/Partners'
import Faq from './components/Faq'
import Contact from './components/Contact'
import Footer from './components/Footer'
import CategoryPage from './components/CategoryPage'
import ProductPage from './components/ProductPage'
import NotFound from './components/NotFound'
import SEO from './components/SEO'
import Admin from './components/Admin'
import { NavigateContext } from './components/Link'
import { parseRoute, type Route } from './router'
import { getPageMeta } from './seo-data'
import { getCategoryBySlug, getProductBySlug, replaceProducts, type Product } from './data/products'

type SectionTarget = { id: string; key: number }

type AppProps = {
  /** Supplied when rendering on the server, where window is unavailable. */
  initialRoute?: Route
}

function App({ initialRoute }: AppProps) {
  const [route, setRoute] = useState<Route>(() => initialRoute ?? parseRoute(window.location.pathname))
  const [target, setTarget] = useState<SectionTarget | null>(null)
  const [, refreshContent] = useState(0)
  const isFirstRun = useRef(true)

  useEffect(() => {
    fetch('/api/content')
      .then((response) => (response.ok ? response.json() : Promise.reject(new Error('content unavailable'))))
      .then((content: { products?: Product[] }) => {
        if (Array.isArray(content.products)) {
          replaceProducts(content.products)
          refreshContent((value) => value + 1)
        }
      })
      .catch(() => undefined)
  }, [])

  useEffect(() => {
    const onPopState = () => {
      setRoute(parseRoute(window.location.pathname))
      setTarget(null)
    }
    window.addEventListener('popstate', onPopState)
    return () => window.removeEventListener('popstate', onPopState)
  }, [])

  const navigate = useCallback((to: string) => {
    const [rawPath, hash] = to.split('#')
    const path = rawPath || '/'
    const samePath = path === window.location.pathname

    if (!samePath) {
      window.history.pushState({}, '', to)
      setRoute(parseRoute(path))
    }
    setTarget(hash ? { id: hash, key: Date.now() } : null)
  }, [])

  useEffect(() => {
    if (isFirstRun.current) {
      isFirstRun.current = false
      return
    }
    if (route.name === 'home' && target) {
      document.getElementById(target.id)?.scrollIntoView({ behavior: 'smooth' })
      return
    }
    window.scrollTo({ top: 0, behavior: 'smooth' })
  }, [route, target])

  const category = useMemo(
    () => (route.name === 'category' ? getCategoryBySlug(route.slug) : undefined),
    [route]
  )
  const product = useMemo(
    () => (route.name === 'product' ? getProductBySlug(route.slug) : undefined),
    [route]
  )

  if (route.name === 'admin') return <Admin />

  const resolvedRoute: Route =
    route.name === 'category' && !category ? { name: 'notFound' } : route.name === 'product' && !product ? { name: 'notFound' } : route

  const meta = getPageMeta(resolvedRoute)

  return (
    <NavigateContext.Provider value={navigate}>
      <SEO
        title={meta.title}
        description={meta.description}
        keywords={meta.keywords}
        ogImage={meta.ogImage}
      />

      {resolvedRoute.name !== 'notFound' && <Header />}

      {resolvedRoute.name === 'home' && (
        <main className="min-h-screen bg-white">
          <Hero />
          <Services />
          <Portfolio />
          <About />
          <Partners />
          <Faq />
          <Contact />
        </main>
      )}

      {resolvedRoute.name === 'category' && category && (
        <CategoryPage category={category.category} items={category.items} />
      )}

      {resolvedRoute.name === 'product' && product && (
        <ProductPage product={product} />
      )}

      {resolvedRoute.name === 'notFound' && <NotFound />}

      {resolvedRoute.name !== 'notFound' && <Footer />}
    </NavigateContext.Provider>
  )
}

export default App
